export const RUNTIME_FOV_MIN = 30;
export const RUNTIME_FOV_MAX = 110;
const DEFAULT_RUNTIME_FOV = 75;
export const DEFAULT_RUNTIME_CAMERA_HEIGHT = 1.7;
export const RUNTIME_CAMERA_HEIGHT_MIN = 1.0;
export const RUNTIME_CAMERA_HEIGHT_MAX = 8.0;
export const RUNTIME_CAMERA_HEIGHT_STEP = 0.05;

type PerspectiveLikeCamera = {
  fov: number;
  position?: {
    y: number;
  };
  updateProjectionMatrix?: () => void;
};

type CameraHeightLike = {
  position?: {
    y: number;
  };
};

type PlayerColliderLike = {
  start?: {
    y: number;
  };
  end?: {
    y: number;
  };
};

type RuntimeCameraHeightState = {
  runtimeCameraHeight?: number | null;
  galleryData?: {
    data?: {
      mood?: number;
    };
  } | null;
  spaceData?: {
    data?: {
      moods?: Array<{ cameraHeight?: number } | null>;
    };
  } | null;
  camera?: CameraHeightLike | null;
  physicsService?: {
    playerCollider?: PlayerColliderLike | null;
  } | null;
  renderService?: {
    addToRenderQueue?: (frames?: number) => void;
  } | null;
};

const hasFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isPerspectiveLikeCamera = (camera: unknown): camera is PerspectiveLikeCamera => {
  return Boolean(
    camera &&
    typeof (camera as PerspectiveLikeCamera).fov === 'number' &&
    typeof (camera as PerspectiveLikeCamera).updateProjectionMatrix === 'function'
  );
};

export const clampRuntimeFov = (value: number): number => {
  if (!Number.isFinite(value)) {
    return DEFAULT_RUNTIME_FOV;
  }

  return Math.min(RUNTIME_FOV_MAX, Math.max(RUNTIME_FOV_MIN, value));
};

export const clampRuntimeCameraHeight = (value: number): number => {
  if (!Number.isFinite(value)) {
    return DEFAULT_RUNTIME_CAMERA_HEIGHT;
  }

  return Math.min(RUNTIME_CAMERA_HEIGHT_MAX, Math.max(RUNTIME_CAMERA_HEIGHT_MIN, value));
};

const getRuntimeCameraHeightOverride = (appData: RuntimeCameraHeightState | null | undefined): number | null => {
  if (!hasFiniteNumber(appData?.runtimeCameraHeight)) {
    return null;
  }

  return clampRuntimeCameraHeight(appData.runtimeCameraHeight);
};

export const getMoodCameraHeight = (mood: { cameraHeight?: number } | null | undefined): number => {
  return hasFiniteNumber(mood?.cameraHeight)
    ? clampRuntimeCameraHeight(mood.cameraHeight)
    : DEFAULT_RUNTIME_CAMERA_HEIGHT;
};

const getActiveMoodCameraHeight = (appData: RuntimeCameraHeightState | null | undefined): number => {
  const moods = appData?.spaceData?.data?.moods;
  if (!Array.isArray(moods) || moods.length === 0) {
    return DEFAULT_RUNTIME_CAMERA_HEIGHT;
  }

  const requestedMood = appData?.galleryData?.data?.mood;
  const mood = Number.isInteger(requestedMood) && requestedMood! >= 0
    ? moods[requestedMood!]
    : moods[0];
  return getMoodCameraHeight(mood || moods[0]);
};

export const getActiveRenderSettingsCamera = (appData: any, debugCameraEnabled: boolean): PerspectiveLikeCamera | null => {
  const candidate = debugCameraEnabled ? appData?.debugCamera : appData?.camera;
  return isPerspectiveLikeCamera(candidate) ? candidate : null;
};

export const getActiveRuntimeFov = (appData: any, debugCameraEnabled: boolean): number => {
  const camera = getActiveRenderSettingsCamera(appData, debugCameraEnabled);
  return camera ? clampRuntimeFov(camera.fov) : DEFAULT_RUNTIME_FOV;
};

export const applyRuntimeFov = (appData: any, debugCameraEnabled: boolean, nextValue: number): number => {
  const camera = getActiveRenderSettingsCamera(appData, debugCameraEnabled);
  const clampedValue = clampRuntimeFov(nextValue);

  if (!camera) {
    return clampedValue;
  }

  camera.fov = clampedValue;
  camera.updateProjectionMatrix?.();
  appData?.renderService?.addToRenderQueue?.(30);

  return clampedValue;
};

export const getCurrentRuntimeCameraHeight = (appData: RuntimeCameraHeightState | null | undefined): number => {
  const runtimeOverride = getRuntimeCameraHeightOverride(appData);
  if (runtimeOverride !== null) {
    return runtimeOverride;
  }

  const colliderHeight = appData?.physicsService?.playerCollider?.end?.y;
  if (hasFiniteNumber(colliderHeight)) {
    return clampRuntimeCameraHeight(colliderHeight);
  }

  const cameraHeight = appData?.camera?.position?.y;
  if (hasFiniteNumber(cameraHeight)) {
    return clampRuntimeCameraHeight(cameraHeight);
  }

  return DEFAULT_RUNTIME_CAMERA_HEIGHT;
};

export const getConfiguredRuntimeCameraHeight = (appData: RuntimeCameraHeightState | null | undefined): number => {
  return getRuntimeCameraHeightOverride(appData) ?? getActiveMoodCameraHeight(appData);
};

export const getPersistedCameraHeight = (appData?: RuntimeCameraHeightState | null): number => {
  return getActiveMoodCameraHeight(appData);
};

export const applyRuntimeCameraHeight = (appData: RuntimeCameraHeightState | null | undefined, nextValue: number): number => {
  const clampedValue = clampRuntimeCameraHeight(nextValue);

  if (!appData) {
    return clampedValue;
  }

  appData.runtimeCameraHeight = clampedValue;

  if (appData.physicsService?.playerCollider?.start) {
    appData.physicsService.playerCollider.start.y = clampedValue;
  }

  if (appData.physicsService?.playerCollider?.end) {
    appData.physicsService.playerCollider.end.y = clampedValue;
  }

  if (appData.camera?.position) {
    appData.camera.position.y = clampedValue;
  }

  appData.renderService?.addToRenderQueue?.(30);

  return clampedValue;
};

export const clearRuntimeCameraHeight = (appData: RuntimeCameraHeightState | null | undefined): number => {
  if (appData) {
    appData.runtimeCameraHeight = null;
  }

  return getConfiguredRuntimeCameraHeight(appData);
};
